import { CryptoProGateway } from './cryptopro-gateway';
import { ReportFormSignApi } from './report-form-sign-api';
import type { SignProfile } from './sign-types';
import { ReportFormSignView } from './report-form-sign-view';

export class ReportFormSignPresenter {
  private preparedPayload: { id: string; content: string } | null = null;

  constructor(
    private readonly view: ReportFormSignView,
    private readonly gateway: CryptoProGateway,
    private readonly api: ReportFormSignApi,
    private readonly options: {
      signProfile: SignProfile;
      helpUrl: string;
      onSigned?: () => void;
    },
  ) {}

  init(): void {
    this.view.setHelpUrl(this.options.helpUrl);
    this.view.bindConfirm(async (thumbprint) => {
      await this.confirmSign(thumbprint);
    });
  }

  async handlePrepareSuccess(response: unknown): Promise<void> {
    const prepared = normalizePrepareResponse(response);
    if (!prepared) {
      return;
    }

    let content = prepared.content;
    if (!content && prepared.pdfUrl) {
      try {
        content = await fetchPdfAsBase64(prepared.pdfUrl);
      } catch (error) {
        this.view.showError(normalizeError(error) || 'Не удалось загрузить PDF для подписи.');
        return;
      }
    }

    if (!content || !prepared.id) {
      this.view.showError(
        'Сервер не вернул данные для подписи (ожидаются data.id и data.pdfUrl либо PDF в base64).',
      );
      return;
    }

    this.preparedPayload = {
      id: prepared.id,
      content,
    };

    this.view.clearError();
    this.view.setDocumentInfo(
      prepared.reportPdfFileName,
      prepared.reportPeriod,
      prepared.generatedAt,
      prepared.pdfUrl,
    );
    this.view.openModal();
    await this.loadCertificates();
  }

  private async loadCertificates(): Promise<void> {
    this.view.setModalProcessing(true);
    this.view.clearError();
    try {
      await this.gateway.checkAvailability();
      const certs = await this.gateway.getCertificates();
      this.view.renderCerts(certs);
    } catch {
      this.view.renderCerts([]);
    } finally {
      this.view.setModalProcessing(false);
    }
  }

  private async confirmSign(thumbprint: string | null): Promise<void> {
    if (!this.preparedPayload) {
      this.view.showError('Нет данных сформированного отчёта. Сначала сохраните отчёт.');
      return;
    }

    if (!thumbprint) {
      this.view.showError('Выберите сертификат для подписи.');
      return;
    }

    const cert = this.view.findCertByThumbprint(thumbprint);
    if (!cert) {
      this.view.showError('Не удалось найти выбранный сертификат. Обновите список.');
      return;
    }

    this.view.setModalProcessing(true);
    this.view.clearError();
    try {
      await this.gateway.checkAvailability();
      const signature = await this.gateway.signDetached(
        this.preparedPayload.content,
        cert.thumbprint,
        this.options.signProfile,
      );

      const response = await this.api.signReport({
        id: this.preparedPayload.id,
        content: this.preparedPayload.content,
        signature,
      });

      if (!response.ok) {
        throw new Error(`Backend вернул статус ${response.status} при сохранении подписи.`);
      }

      const archiveUrl = this.api.getSignedArchiveUrl(this.preparedPayload.id);
      this.view.showSuccess(
        'Отчёт успешно подписан и отправлен.',
        archiveUrl,
        new Date().toISOString(),
        cert.serialNumber,
        extractSignerName(cert.subjectName),
      );
      this.options.onSigned?.();
    } catch (error) {
      this.view.showError(normalizeError(error));
    } finally {
      this.view.setModalProcessing(false);
    }
  }
}

type NormalizedPrepare = {
  id: string;
  content: string;
  pdfUrl: string | null;
  reportPdfFileName: string;
  reportPeriod: string;
  generatedAt: string;
};

function normalizePrepareResponse(response: unknown): NormalizedPrepare | null {
  if (!response || typeof response !== 'object') {
    return null;
  }

  const root = response as Record<string, unknown>;

  let id = '';
  let content = '';
  let pdfUrl: string | null = null;

  const nested = root.data;
  if (nested && typeof nested === 'object') {
    const d = nested as Record<string, unknown>;
    if (d.id != null) {
      id = String(d.id);
    }
    if (typeof d.pdfUrl === 'string') {
      pdfUrl = d.pdfUrl;
    }
    if (typeof d.content === 'string') {
      content = d.content;
    }
    if (typeof d.reportPdfBase64 === 'string' && !content) {
      content = d.reportPdfBase64;
    }
  }

  if (!id) {
    id = String(root.id ?? root.reportId ?? '');
  }
  if (!content) {
    content = String(root.content ?? root.reportPdfBase64 ?? '');
  }
  if (!pdfUrl && typeof root.pdfUrl === 'string') {
    pdfUrl = root.pdfUrl;
  }

  const reportPdfFileName =
    (typeof root.reportPdfFileName === 'string' && root.reportPdfFileName) ||
    (typeof root.fileName === 'string' && root.fileName) ||
    fileNameFromPdfUrl(pdfUrl) ||
    'Отчёт.pdf';
  const reportPeriod =
    (typeof root.reportPeriod === 'string' && root.reportPeriod) ||
    (typeof root.period === 'string' && root.period) ||
    '-';
  const generatedAt =
    (typeof root.generatedAt === 'string' && root.generatedAt) || new Date().toISOString();

  return {
    id,
    content,
    pdfUrl,
    reportPdfFileName,
    reportPeriod,
    generatedAt,
  };
}

function fileNameFromPdfUrl(url: string | null): string | null {
  if (!url) {
    return null;
  }
  try {
    const u = new URL(url, window.location.origin);
    const seg = u.pathname.split('/').filter(Boolean).pop();
    if (seg && /\.pdf$/i.test(seg)) {
      return decodeURIComponent(seg);
    }
  } catch {
    /* ignore */
  }
  return null;
}

async function fetchPdfAsBase64(pdfUrl: string): Promise<string> {
  const res = await fetch(pdfUrl, { method: 'GET' });
  if (!res.ok) {
    throw new Error(`Не удалось загрузить PDF (${res.status})`);
  }
  const buf = await res.arrayBuffer();
  const bytes = new Uint8Array(buf);
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

function normalizeError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function extractSignerName(subjectName: string): string {
  const parts = subjectName.split(',');
  const found = parts.find((part) => part.trim().startsWith('CN='));
  return found ? found.split('=').slice(1).join('=').trim() : subjectName;
}
